import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { plyxApi, plyxMediaUrl } from "@/lib/plyxApi";
import { usePlyxAuth } from "@/context/PlyxAuthContext";
import { Loader2, Trophy, Flame, Sparkles, Music, Globe, Shield, Smile, Plus, Coins, Heart, Users } from "lucide-react";
import { toast } from "sonner";
import WeeklySpotlight from "@/components/plyx/WeeklySpotlight";

/**
 * Plyx Challenges hub.
 *   /plyx/challenges
 *
 * One page, two slices:
 *   - "Live now" — currently accepting entries
 *   - "Champions corner" — recently-ended winners (judging+ended)
 */

const TEMPLATES = [
  { code: "talent",  label: "Talent Show",  Icon: Sparkles, gradient: "linear-gradient(135deg,#FF2D7A,#FFB42D)" },
  { code: "country", label: "Country Battle", Icon: Globe,   gradient: "linear-gradient(135deg,#00E0C7,#FFB42D)" },
  { code: "club",    label: "Club Derby",   Icon: Shield,    gradient: "linear-gradient(135deg,#A45DFF,#FF2D7A)" },
  { code: "dance",   label: "Dance Battle", Icon: Music,     gradient: "linear-gradient(135deg,#FF6B00,#FFD700)" },
  { code: "comedy",  label: "Comedy Roast", Icon: Smile,     gradient: "linear-gradient(135deg,#00E0C7,#A45DFF)" },
  { code: "general", label: "Other",        Icon: Flame,     gradient: "linear-gradient(135deg,#737B7E,#0A0A0A)" },
];
const TEMPLATE_BY_CODE = Object.fromEntries(TEMPLATES.map((t) => [t.code, t]));

const PlyxChallenges = () => {
  const { user } = usePlyxAuth();
  const [filter, setFilter] = useState("active");  // active | live | judging | ended
  const [templateFilter, setTemplateFilter] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const q = new URLSearchParams({ status: filter });
    if (templateFilter) q.set("template", templateFilter);
    plyxApi.get(`/plyx/challenges?${q.toString()}`)
      .then((r) => setItems(r.data?.items || []))
      .catch(() => toast.error("Could not load challenges"))
      .finally(() => setLoading(false));
  }, [filter, templateFilter]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="plyx-challenges-page">
      {/* Hero */}
      <div className="relative overflow-hidden border-b border-white/10">
        <div
          className="absolute inset-0 opacity-30 pointer-events-none"
          style={{ background: "radial-gradient(circle at 70% 0%, #FFB42D 0%, transparent 50%), radial-gradient(circle at 20% 100%, #FF2D7A 0%, transparent 50%)" }}
        />
        <div className="relative max-w-4xl mx-auto px-5 py-10 sm:py-14">
          <div className="flex items-baseline justify-between gap-4 flex-wrap">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/15 text-[11px] uppercase tracking-[0.3em] font-semibold text-[#FFB42D] mb-3">
                <Trophy className="w-3 h-3"/> Plyx Challenges
              </div>
              <h1 className="font-display text-4xl sm:text-6xl tracking-tight">
                Show up. Stand out. <span className="bg-clip-text text-transparent" style={{ backgroundImage: "#E85D4E" }}>Win.</span>
              </h1>
              <p className="text-sm text-white/60 max-w-xl mt-3">
                Talent shows, country derbies, club battles. Enter your video, rally your fans, take home the prize pool.
              </p>
            </div>
            {user && (
              <Link
                to="/plyx/challenges/new"
                data-testid="plyx-challenge-new-btn"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-white text-sm shrink-0"
                style={{ background: "#E85D4E" }}
              >
                <Plus className="w-4 h-4"/> Create a challenge
              </Link>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-5 py-8 pb-24">
        <WeeklySpotlight />

        {/* Filter bar */}
        <div className="flex items-center gap-2 flex-wrap mb-6">
          <div className="inline-flex bg-black/40 border border-white/10 rounded-full p-1" data-testid="plyx-challenges-status-filter">
            {[
              { v: "active", l: "Open" },
              { v: "live",   l: "Live now" },
              { v: "ended",  l: "Champions" },
            ].map((o) => (
              <button
                key={o.v}
                onClick={() => setFilter(o.v)}
                data-testid={`plyx-challenges-filter-${o.v}`}
                className={`px-4 py-1.5 rounded-full text-xs font-semibold ${
                  filter === o.v ? "bg-white text-[#0A0A0A]" : "text-white/70 hover:text-white"
                }`}
              >
                {o.l}
              </button>
            ))}
          </div>
          <div className="flex gap-1.5 flex-wrap" data-testid="plyx-challenges-template-filter">
            {TEMPLATES.map(({ code, label, Icon }) => (
              <button
                key={code}
                type="button"
                onClick={() => setTemplateFilter(templateFilter === code ? null : code)}
                data-testid={`plyx-challenges-template-${code}`}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                  templateFilter === code
                    ? "bg-white text-white border-white"
                    : "border-white/15 text-white/70 hover:text-white hover:border-white/30"
                }`}
              >
                <Icon className="w-3 h-3"/> {label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin opacity-60"/></div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-white/5 p-12 text-center" data-testid="plyx-challenges-empty">
            <Trophy className="w-12 h-12 mx-auto text-white/30 mb-3"/>
            <div className="font-semibold">No challenges in this view</div>
            <div className="text-xs text-white/50 mt-1">Be the first — start one and rally the feed.</div>
            {user && (
              <Link to="/plyx/challenges/new" className="mt-5 inline-block px-5 py-2 rounded-full bg-white text-white text-xs font-bold">
                Create a challenge
              </Link>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="plyx-challenges-grid">
            {items.map((c) => (<ChallengeCard key={c.challenge_id} c={c}/>))}
          </div>
        )}
      </div>
    </div>
  );
};

const ChallengeCard = ({ c }) => {
  const tmpl = TEMPLATE_BY_CODE[c.template] || TEMPLATE_BY_CODE.general;
  const { Icon } = tmpl;
  const endsAt = c.ends_at ? new Date(c.ends_at) : null;
  const isLive = c.status === "live";
  const isEnded = c.status === "ended";
  return (
    <Link
      to={`/plyx/challenges/${c.challenge_id}`}
      data-testid={`plyx-challenge-card-${c.challenge_id}`}
      className="relative rounded-2xl overflow-hidden border border-white/10 hover:border-white/30 transition-all hover:scale-[1.01] group"
      style={{ background: c.banner_url ? "#000" : tmpl.gradient }}
    >
      {c.banner_url && (
        <img src={plyxMediaUrl(c.banner_url)} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-50 transition-opacity" loading="lazy"/>
      )}
      <div className="relative p-5 flex flex-col gap-3 min-h-[200px]">
        <div className="flex items-center justify-between">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/50 backdrop-blur text-[10px] uppercase tracking-[0.2em] font-bold">
            <Icon className="w-3 h-3"/> {tmpl.label}
          </div>
          {isLive && (
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-600 text-[10px] uppercase font-bold animate-pulse">
              <span className="w-1.5 h-1.5 rounded-full bg-white"/> LIVE
            </div>
          )}
          {isEnded && (
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/60 text-[10px] uppercase font-bold">
              <Trophy className="w-3 h-3 text-[#FFD700]"/> Ended
            </div>
          )}
        </div>
        <h3 className="font-display text-2xl tracking-tight leading-tight line-clamp-2 text-white drop-shadow-lg">{c.title}</h3>
        {c.country_tags?.length > 0 && (
          <div className="flex gap-1 text-xs font-semibold">
            {c.country_tags.slice(0, 4).map((cc, i) => (
              <span key={i} className="px-2 py-0.5 rounded-full bg-white/15 backdrop-blur">{cc}</span>
            ))}
          </div>
        )}
        <div className="mt-auto flex items-center gap-4 text-xs text-white/90">
          <span className="inline-flex items-center gap-1"><Users className="w-3.5 h-3.5"/> {c.entry_count}</span>
          <span className="inline-flex items-center gap-1"><Heart className="w-3.5 h-3.5"/> {c.total_hearts}</span>
          {c.prize_pool_coins > 0 && (
            <span className="inline-flex items-center gap-1 text-[#FFB42D] font-semibold">
              <Coins className="w-3.5 h-3.5"/> {c.prize_pool_coins.toLocaleString()}
            </span>
          )}
          {endsAt && isLive && (
            <span className="ml-auto text-white/70 text-[11px]">
              ends {endsAt.toLocaleDateString(undefined, { month: "short", day: "numeric" })}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
};

export default PlyxChallenges;
